import { observer } from 'mobx-react-lite'
import { useNavigate, useOutletContext } from 'react-router'
import type { JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import {
  CardItemContent,
  CardList,
  CardListItem,
  CardSubtitle,
  CardTitle,
} from '@/components/Card'
import {
  WizardContentBody,
  WizardContentHeader,
  WizardContentHeaderDescription,
  WizardContentHeaderTitle,
  WizardPageAnimatedContent,
  WizardPageSingleColumnRow,
} from '@/components/Wizard'
import type { TaxJobDetailsViewModel } from '@/features/jobs/details/tax-type/TaxJobDetailsViewModel'
import { links } from '@/utils/link'
import { MeasureOneExperience } from '@/features/jobs/details/tax-type/info-gathering/measure-one/mappers'
import styles from '../MeasureOne.module.css'
import { MeasureOneKicker } from '@/features/jobs/details/tax-type/info-gathering/measure-one/components/MeasureOneKicker'
import { MeasureOneSpinner } from '@/features/jobs/details/tax-type/info-gathering/measure-one/components/MeasureOneSpinnner'

const accountTypes = [
  {
    title: 'Tax documents',
    description: 'Pull available tax form directly from connected providers',
    experience: MeasureOneExperience.Employment,
  },
  {
    title: 'Bank documents',
    description:
      'Pull available bank statements directly from your financial accounts',
    experience: MeasureOneExperience.BankBrokerage,
  },
]

export default observer(function MeasureOneSelectAccountTypeContainer() {
  const navigate = useNavigate()
  const vm = useOutletContext<JobDetailsViewModel>()
  const taxVm = vm.typeVm as TaxJobDetailsViewModel
  const measureOneVm = taxVm.infoGatheringVm.measureOneVm

  const onPress = async (experience: MeasureOneExperience) => {
    await measureOneVm.initializeMeasureOneImportFlow(experience)

    void navigate(links.measureOneSelectConnection(vm.job.id))
  }

  return (
    <>
      <WizardPageSingleColumnRow>
        <WizardPageAnimatedContent
          animationKey="default"
          animationDirection="forward"
        >
          <WizardContentHeader>
            <ExperienceTypeHeader />
          </WizardContentHeader>
        </WizardPageAnimatedContent>
      </WizardPageSingleColumnRow>

      <WizardPageSingleColumnRow>
        <WizardPageAnimatedContent
          animationKey="default"
          animationDirection="forward"
        >
          <WizardContentBody>
            {measureOneVm.initializeMeasureOneImportFlow.pending ? (
              <MeasureOneSpinner />
            ) : (
              <CardList aria-label="Account types">
                {accountTypes.map(({ title, description, experience }) => (
                  <MeasureOneAccountItem
                    loading={
                      measureOneVm.initializeMeasureOneImportFlow.pending
                    }
                    title={title}
                    description={description}
                    action={() => onPress(experience)}
                    key={title}
                  />
                ))}
              </CardList>
            )}
          </WizardContentBody>
        </WizardPageAnimatedContent>
      </WizardPageSingleColumnRow>
    </>
  )
})

function ExperienceTypeHeader() {
  return (
    <div className={styles.content}>
      <MeasureOneKicker />

      <WizardContentHeaderTitle>
        Select your account type
      </WizardContentHeaderTitle>
      <WizardContentHeaderDescription>
        Choose the type of account you want to connect through MeasureOne.
      </WizardContentHeaderDescription>
    </div>
  )
}

interface MeasureOneAccountItemProps {
  title: string
  description: string
  action: () => unknown
  loading: boolean
}

function MeasureOneAccountItem({
  title,
  description,
  action,
  loading,
}: MeasureOneAccountItemProps) {
  return (
    <CardListItem isDisabled={loading} onAction={action}>
      <CardItemContent align="center" isInteractive>
        <CardTitle>{title}</CardTitle>
        <CardSubtitle>{description}</CardSubtitle>
      </CardItemContent>
    </CardListItem>
  )
}
